import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Save, Send, RotateCcw, Loader2, CheckCircle2, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Admin control for the Resend email provider. Lets admins rotate the API
 * key + sender address without touching env vars or redeploying, and fire
 * a live test send to see the exact provider response.
 *
 * Storage strategy: DB values in `site_settings._id="email_provider"` win
 * over env vars; leaving both fields blank falls back to the env vars, so
 * clearing the override never lands the app in a broken state.
 */
export default function EmailProviderPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [sender, setSender] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-provider");
      setSettings(r.data);
      setSender(r.data.sender_email || "");
      setNewKey(""); // never prefill the key input
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load provider settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Only send the key when the admin actually typed a new one; sending
      // `null` preserves the existing value.
      const body = { sender_email: sender };
      if (newKey.trim()) body.resend_api_key = newKey.trim();
      await api.put("/admin/email-provider", body);
      toast.success("Email provider settings saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!confirm("Clear the DB override and fall back to environment variables?")) return;
    setSaving(true);
    try {
      await api.put("/admin/email-provider", { resend_api_key: "", sender_email: "" });
      toast.success("Override cleared — using env vars", {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const body = {};
              if (newKey.trim()) body.resend_api_key = newKey.trim();
              if (sender.trim()) body.sender_email = sender.trim();
              if (Object.keys(body).length) await api.put("/admin/email-provider", body);
              toast.success("Override restored");
              await load();
            } catch (e) {
              toast.error("Couldn't restore override — re-enter manually");
            }
          },
        },
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear override");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testTo.trim()) return;
    setTestBusy(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/email-provider/test-send", { to: testTo.trim() });
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Email provider accepted the test email for ${testTo.trim()}`);
      else toast.error("Test send failed — see status below");
    } catch (e) {
      setTestResult({ ok: false, log: { status: "failed", error: e?.response?.data?.detail || String(e) } });
    } finally {
      setTestBusy(false);
    }
  };

  return (
    <div
      data-testid="email-provider-panel"
      className="mb-6 border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900"
    >
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left border-b border-slate-200 dark:border-slate-800"
        data-testid="email-provider-toggle"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400"/> : <ChevronRight className="w-4 h-4 text-slate-400"/>}
          <KeyRound className="w-4 h-4 text-sky-600 dark:text-sky-400"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Email provider (Resend)</div>
            {settings && !loading && (
              <div className="text-[11px] text-slate-500 dark:text-slate-500 font-mono">
                {settings.resend_api_key_masked || <em>no key</em>} · from <span className="text-slate-700 dark:text-slate-300">{settings.sender_email}</span>
                {settings.override_active
                  ? <span className="ml-2 text-emerald-600 dark:text-emerald-400">override active</span>
                  : <span className="ml-2 text-slate-400 dark:text-slate-500">using env vars</span>}
              </div>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* API key */}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Resend API key
            </label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                data-testid="email-provider-api-key"
                type="password"
                autoComplete="off"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder={settings?.resend_api_key_masked ? `Current: ${settings.resend_api_key_masked} — paste new key to rotate` : "re_…"}
                className="font-mono text-xs"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Leave blank to keep the current key. Get one at{" "}
              <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-sky-700 dark:text-sky-400 hover:underline">resend.com/api-keys</a>{" "}
              (use <strong>Full access</strong>, not sandbox).
            </p>
          </div>

          {/* Sender email */}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Sender email (must be on a verified domain)
            </label>
            <Input
              data-testid="email-provider-sender"
              value={sender}
              onChange={e => setSender(e.target.value)}
              placeholder="no-reply@hospicesynopsis.com"
              className="mt-1 font-mono text-xs"
            />
          </div>

          {/* Save / Clear */}
          <div className="flex items-center gap-2">
            <Button
              data-testid="email-provider-save"
              onClick={save}
              disabled={saving || (!newKey.trim() && sender === (settings?.sender_email || ""))}
              className="bg-sky-700 hover:bg-sky-600 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Save className="w-4 h-4 mr-2"/>}
              Save
            </Button>
            {settings?.override_active && (
              <Button
                data-testid="email-provider-clear"
                onClick={clearOverride}
                disabled={saving}
                variant="outline"
                className="border-slate-300 dark:border-slate-700"
              >
                <RotateCcw className="w-4 h-4 mr-2"/>Fall back to env vars
              </Button>
            )}
          </div>

          {/* Test send */}
          <div className="mt-6 pt-4 border-t border-dashed border-slate-200 dark:border-slate-800">
            <label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Send a test email
            </label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                data-testid="email-provider-test-to"
                type="email"
                value={testTo}
                onChange={e => setTestTo(e.target.value)}
                placeholder="you@example.com"
                className="flex-1"
              />
              <Button
                data-testid="email-provider-test-send"
                onClick={runTest}
                disabled={testBusy || !testTo.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {testBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
                Send
              </Button>
            </div>
            {testResult && (
              <div
                data-testid="email-provider-test-result"
                className={`mt-3 text-sm rounded-md p-3 border ${
                  testResult.ok
                    ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300"
                    : "border-red-200 dark:border-red-800 bg-red-50/70 dark:bg-red-950/30 text-red-800 dark:text-red-300"
                }`}
              >
                <div className="inline-flex items-center gap-2 font-semibold">
                  {testResult.ok ? <CheckCircle2 className="w-4 h-4"/> : <AlertCircle className="w-4 h-4"/>}
                  {testResult.ok ? "Accepted by email provider" : "Failed"}
                  {testResult.log?.resend_id && (
                    <span className="font-mono text-xs opacity-80">id={testResult.log.resend_id}</span>
                  )}
                </div>
                {testResult.log?.error && (
                  <pre className="mt-2 text-[11px] font-mono whitespace-pre-wrap break-words">{testResult.log.error}</pre>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
